import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import eventService from '../services/eventService';

const eventsCollectionName = 'events';

export const useEvent = () => {
  return useQuery({
    queryKey: [eventsCollectionName],
    queryFn: () => eventService.getAll(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useEventByRole = ({ user, dateRange, eventTypeId, divisionId, teamId, spendTypeId, activityTypeId, enabled = true }) => {
  return useQuery({
    queryKey: ['events', user?.uid, dateRange, eventTypeId, divisionId, teamId, spendTypeId, activityTypeId],
    queryFn: () => eventService.fetchEventsByRole({ user, dateRange, eventTypeId, divisionId, teamId, spendTypeId, activityTypeId }),
    enabled: enabled && !!user?.uid,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useAddEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: eventService.add,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [eventsCollectionName] });
    },
  }); 
};

export const useEditEvent = (eventId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: eventService.edit,
    onSuccess: () => {
      queryClient.invalidateQueries(["event", eventId]);
      queryClient.invalidateQueries(["events"]);
    },
  });
};

export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: eventService.delete,
    onSuccess: () => {
      // Invalidate both events and deleted_events queries
      queryClient.invalidateQueries({ queryKey: [eventsCollectionName] });
      queryClient.invalidateQueries({ queryKey: ['deleted_events'] });
    },
  });
};

export const useGetEventDataById = (eventId) => {
  return useQuery({
    queryKey: ['event', eventId],
    queryFn: () => eventService.getEventDataById(eventId),
    enabled: !!eventId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useGetEventsDataWithEveryEventTheirAttendees = () => {
  return useQuery({
    queryKey: ['eventsWithAttendees'],
    queryFn: () => eventService.getEventsDataWithEveryEventTheirAttendees(), 
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};
export const useUpdateEventStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, status }) => eventService.updateEventStatus(eventId, status),
    onSuccess: (_, { eventId }) => {
      // Invalidate both the specific event and the events list
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
};

